import merge from 'lodash/merge';

import { createDashHttpClient } from '@/apis/dash.http';
import { env } from '@/env';

export const http = createDashHttpClient(env.dashboardApiUrl);
const abortControllers = new Map();

function getAndSetSignal({
  brandIds,
  competitorSourceAccountIds,
  channels,
  metrics,
  reportType,
  aggregateBy,
  sourceAdAccountIds,
  sourceCampaignIds,
}) {
  const cancelData = {
    brandIds,
    competitorSourceAccountIds,
    channels,
    metrics,
    reportType,
    aggregateBy,
    sourceAdAccountIds,
    sourceCampaignIds,
  };
  const key = JSON.stringify(cancelData);
  abortControllers.get(key)?.abort();
  const controller = new AbortController();
  abortControllers.set(key, controller);
  return controller.signal;
}

const api = {
  // Dashboards
  getDashboards(params) {
    return http.get('/dashboards', {
      params,
    });
  },
  getDashboard({ dashboardId }) {
    return http.get(`/dashboards/${dashboardId}`);
  },
  createDashboard({ data, params }) {
    return http.post(
      '/dashboards',
      {
        ...data,
      },
      {
        params,
      },
    );
  },
  updateDashboard({ dashboardId, data }) {
    return http.patch(`/dashboards/${dashboardId}`, {
      ...data,
    });
  },
  deleteDashboard({ dashboardId }) {
    return http.delete(`/dashboards/${dashboardId}`);
  },
  // Reports
  getMetricTypes() {
    return http.get('/metric_types');
  },
  getMetricDetails(params, config = {}) {
    return http.get('/metric_details', merge({ params }, config));
  },
  getComparableMetrics() {
    return http.get(`/comparable_metrics`);
  },
  getReport({ dashboardId, reportId, signal, params }) {
    return http.get(`/dashboards/${dashboardId}/reports/${reportId}`, { params, signal });
  },
  getGenericReportData(
    {
      brandIds,
      competitorSourceAccountIds,
      startDate,
      endDate,
      contextStartDate,
      contextEndDate,
      channels,
      metrics,
      reportType,
      timeScale,
      sortOrder,
      limit,
      aggregateBy,
      sourceAdAccountIds,
      sourceCampaignIds,
      sourceAdCampaigns,
    },
    config = {},
  ) {
    const signal = getAndSetSignal({
      brandIds,
      competitorSourceAccountIds,
      channels,
      metrics,
      reportType,
      aggregateBy,
      sourceAdAccountIds,
      sourceCampaignIds,
      sourceAdCampaigns,
    });

    return http.put(
      '/reports/data',
      {
        competitor_source_accountIds: competitorSourceAccountIds,
        source_ad_account_ids: sourceAdAccountIds,
        source_campaign_ids: sourceCampaignIds,
        source_ad_campaigns: sourceAdCampaigns,
      },
      merge(
        {
          params: {
            brandIds: Array.isArray(brandIds) ? brandIds.join(',') : brandIds,
            startDate,
            endDate,
            contextStartDate,
            contextEndDate,
            channels: Array.isArray(channels) ? channels.join(',') : channels,
            metrics: Array.isArray(metrics) ? metrics.join(',') : metrics,
            reportType,
            timeScale,
            sortOrder,
            limit,
            aggregateBy,
          },
          signal,
        },
        config,
      ),
    );
  },
  createReport({ dashboardId, data }) {
    return http.post(`/dashboards/${dashboardId}/reports`, {
      ...data,
    });
  },
  updateReport({ dashboardId, reportId, data }) {
    return http.patch(`/dashboards/${dashboardId}/reports/${reportId}`, {
      ...data,
    });
  },
  deleteReport({ dashboardId, reportId }) {
    return http.delete(`/dashboards/${dashboardId}/reports/${reportId}`);
  },
  getTopLineStatsForChannel({
    brandIds,
    startDate,
    endDate,
    contextStartDate,
    contextEndDate,
    channels,
    metrics,
    requirePosts,
    aggregateBy = 'BRAND',
    reportType = 'TOTAL_GROUPED_METRIC',
  } = {}) {
    const signal = getAndSetSignal({
      brandIds,
      channels,
      metrics,
    });

    const params = {
      brandIds,
      startDate,
      endDate,
      contextStartDate,
      contextEndDate,
      requirePosts,
      metrics,
      channels,
      reportType,
      aggregateBy,
    };
    return http.get(`/reports/data`, { params, signal });
  },
  getGraphStats(
    { brandIds, startDate, endDate, metrics, channels, timeScale } = {},
    { signal } = {},
  ) {
    const reportType = 'GRAPH';
    const cancelOptions = signal
      ? { signal }
      : {
          signal: getAndSetSignal({
            brandIds,
            channels,
            metrics,
            reportType,
          }),
        };

    const params = {
      brandIds,
      startDate,
      endDate,
      metrics,
      channels,
      timeScale,
      reportType,
    };
    return http.get(`/reports/data`, { params, ...cancelOptions });
  },
  getBenchmarkGraphStats(
    { brandIds, startDate, endDate, metrics, channels, timeScale, benchmarks } = {},
    { signal } = {},
  ) {
    const params = {
      brandIds,
      startDate,
      endDate,
      metrics,
      channels,
      timeScale,
      reportType: 'GRAPH',
      use_benchmark_cache: true,
    };
    return http.put(`/reports/data`, { benchmarks }, { params, signal });
  },
  // Exports
  getDashboardAutomatedExports({ dashboardId }) {
    return http.get(`/dashboards/${dashboardId}/automated_exports`);
  },
  getAllDashboardAutomatedExports() {
    return http.get(`/dashboards/automated_exports`);
  },
  createDashboardAutomatedExports({ dashboardId, autoExport }) {
    return http.post(`/dashboards/${dashboardId}/automated_exports`, autoExport);
  },
  updateDashboardAutomatedExports({ dashboardId, autoExportId, autoExport }) {
    return http.patch(`/dashboards/${dashboardId}/automated_exports/${autoExportId}`, autoExport);
  },
  deleteAutomatedExport({ dashboardId, autoExportId }) {
    return http.delete(`/dashboards/${dashboardId}/automated_exports/${autoExportId}`);
  },
  deleteDashboardAutomatedExportUser({ dashboardId, autoExportId, userId }) {
    return http.delete(
      `/dashboards/${dashboardId}/automated_exports/${autoExportId}/recipients/${userId}`,
    );
  },
  async getReportCsv({
    dashboardId,
    reportId,
    startDate,
    endDate,
    contextStartDate,
    contextEndDate,
    timeScale,
    filterOverrides = {},
  }) {
    const params = {
      start_date: startDate,
      end_date: endDate,
      context_start_date: contextStartDate,
      context_end_date: contextEndDate,
      time_scale: timeScale,
      format: 'csv',
      ...filterOverrides,
    };
    return http.get(`/dashboards/${dashboardId}/reports/${reportId}`, {
      params,
      responseType: 'blob',
    });
  },
  getReportDataCsv({
    brandIds,
    startDate,
    endDate,
    contextStartDate,
    contextEndDate,
    channels,
    metrics,
    reportType,
    sortOrder,
    limit,
    timeScale,
    filenamePrefix,
    csvLayout,
  }) {
    return http.get(
      `/reports/data`,
      merge(
        {
          params: {
            brandIds,
            startDate,
            endDate,
            contextStartDate,
            contextEndDate,
            channels,
            metrics,
            reportType,
            sortOrder,
            limit,
            timeScale,
            filenamePrefix,
            csvLayout,
          },
        },
        {
          params: { format: 'csv' },
          responseType: 'blob',
        },
      ),
    );
  },
  getDashboardSpreadsheet({
    dashboardId,
    startDate,
    endDate,
    contextStartDate,
    contextEndDate,
    format,
    socketId,
    timeScale,
    filterOverrides = {},
  }) {
    const params = {
      start_date: startDate,
      end_date: endDate,
      context_start_date: contextStartDate,
      context_end_date: contextEndDate,
      time_scale: timeScale,
      socket_id: socketId,
      format,
      ...filterOverrides,
    };
    return http.get(`/dashboards/${dashboardId}`, { params });
  },
  // Sharing
  getDashboardUsers({ dashboardId }) {
    return http.get(`/dashboards/${dashboardId}/users`);
  },
  shareDashboard({ dashboardId, userId, userRole }) {
    return http.put(`/dashboards/${dashboardId}/users/${userId}`, {
      user_role: userRole,
    });
  },
  updateDashboardOwner({ dashboardId, userId }) {
    return http.put(`/dashboards/${dashboardId}/owner`, {
      user_id: userId,
    });
  },
  deleteDashboardUser({ dashboardId, userId }) {
    return http.delete(`/dashboards/${dashboardId}/users/${userId}`);
  },
  // Industries
  getIndustries(includeFollowerBuckets = true) {
    return http.get(`/industries?include_follower_buckets=${includeFollowerBuckets}`);
  },
  async getIndustriesBySourceAccount(sourceAccountId) {
    return http.get(`/industries/source_account?source_account_id=${sourceAccountId}`);
  },
  getHubspotIndustries() {
    return http.get('/benchmark/industries');
  },

  // Custom Metrics
  createCustomMetric({ organizationId, params }) {
    return http.post(`organizations/${organizationId}/custom_metric`, params);
  },

  getCustomMetricsUsage({ brandIds }) {
    return http.post(`/custom_metrics/usage`, { brandIds });
  },

  getCustomMetrics({ organizationIds, brandIds, params }) {
    let payload;
    if (organizationIds) payload = { organizationIds };
    else if (brandIds) payload = { brandIds };
    return http.post('/custom_metrics', payload, {
      params,
    });
  },

  updateCustomMetric({ organizationId, metricId, params }) {
    return http.patch(`organizations/${organizationId}/custom_metric/${metricId}`, params);
  },
  deleteCustomMetric({ organizationId, metricId }) {
    return http.delete(`organizations/${organizationId}/custom_metric/${metricId}`);
  },
  // Shareable Dashboard Links
  getShareableDashboardLinks({ userId, dashboardId, brandId }) {
    const params = {
      userId,
      dashboardId,
      brandId,
    };
    return http.get(`/shareable_links`, {
      params,
      camelizeKeys: true,
    });
  },
  getShareableDashboardLink({ shareableLinkId }) {
    return http.get(`/shareable_links/${shareableLinkId}`, { camelizeKeys: true });
  },
  createShareableDashboardLink({
    dashboardId,
    name,
    allowReportingPeriodChange,
    allowFilterChange,
    validUntil,
    defaultFilters,
  }) {
    const data = {
      dashboardId,
      name,
      allowReportingPeriodChange,
      allowFilterChange,
      validUntil,
      defaultFilters,
    };
    return http.post('/shareable_links', data, { camelizeKeys: true });
  },
  updateShareableDashboardLink({
    shareableLinkId,
    name,
    enabled,
    validUntil,
    allowReportingPeriodChange,
    allowFilterChange,
    defaultFilters,
  }) {
    const data = {
      name,
      enabled,
      validUntil,
      allowReportingPeriodChange,
      allowFilterChange,
      defaultFilters,
    };
    return http.patch(`/shareable_links/${shareableLinkId}`, data, { camelizeKeys: true });
  },
  deleteShareableDashboardLink({ shareableLinkId }) {
    return http.delete(`/shareable_links/${shareableLinkId}`, { camelizeKeys: true });
  },
};

export default api;
